import type { MouseEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Business } from "@/types/directory";
import { computeOpenNow } from "@/utils/hours";
import { isPaidPlan } from "@/utils/plans";
import { getBusinessFallbackImage } from "@/utils/fallbackImage";
import Badge from "./Badge";
import LockedFeature from "./LockedFeature";
import RatingStars from "./RatingStars";
import SafeImage from "./SafeImage";

interface BusinessCardProps {
  business: Business;
  /** Make the entire card clickable through to the detail page. */
  clickableCard?: boolean;
}

export default function BusinessCard({ business, clickableCard = false }: BusinessCardProps) {
  const navigate = useNavigate();
  const fallbackImage = getBusinessFallbackImage(business);

  const handleCardClick = (event: MouseEvent<HTMLElement>) => {
    if (!clickableCard) return;
    if ((event.target as HTMLElement).closest("a, button")) return;
    navigate(`/business/${business.slug}`);
  };

  return (
    <article
      onClick={handleCardClick}
      className={`group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200 ${
        clickableCard ? "cursor-pointer" : ""
      }`}
    >
      <Link
        to={`/business/${business.slug}`}
        className="relative block h-48 w-full overflow-hidden"
        aria-label={`View ${business.name}`}
      >
        <SafeImage
          src={business.cover}
          fallback={fallbackImage}
          alt={`${business.name} - ${business.subcategory} in ${business.locationSlug}`}
          title={`${business.name} - ${business.subcategory}`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          {business.sponsored ? <Badge tone="accent">Sponsored</Badge> : null}
          {business.featured && !business.sponsored ? <Badge tone="primary">Featured</Badge> : null}
          {business.verified ? <Badge tone="secondary" icon="ri-verified-badge-fill">Verified</Badge> : null}
        </div>
        {isPaidPlan(business.plan) ? (
          computeOpenNow(business.hours, business.openNow) ? (
            <span className="absolute right-3 top-3 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-50/95 px-2.5 py-1 text-xs font-semibold text-secondary-800">
              <span className="h-1.5 w-1.5 rounded-full bg-secondary-500" />
              Open Now
            </span>
          ) : null
        ) : (
          <LockedFeature compact label="Live hours" className="absolute right-3 top-3" />
        )}
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <Link
          to={`/category/${business.categorySlug}`}
          className="inline-flex w-fit max-w-full items-center gap-1.5 text-xs font-semibold text-secondary-700 transition-colors hover:text-primary-700"
        >
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-price-tag-3-line text-[13px]" />
          </span>
          <span className="truncate">{business.subcategory}</span>
        </Link>

        <h3 className="mt-2 font-heading text-base font-bold leading-snug text-[#2C488E]">
          <Link to={`/business/${business.slug}`} className="transition-colors hover:text-[#233a71]">
            {business.name}
          </Link>
        </h3>

        <p className="mt-1 flex items-center gap-1.5 text-xs text-foreground-500">
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-map-pin-line text-[13px] text-red-500" />
          </span>
          {business.address.split(",").slice(-3).join(",").trim()}
        </p>

        <p className="mt-3 line-clamp-2 min-h-[44px] text-sm leading-relaxed text-foreground-600">
          {business.tagline}
        </p>

        <div className="mt-3 flex h-[54px] flex-wrap content-start gap-1.5 overflow-hidden">
          {business.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-600"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="mt-auto flex flex-col">
        <div className="mt-4 flex min-h-[26px] items-center border-t border-background-200 pt-4">
          <RatingStars rating={business.rating} count={business.reviewCount} />
        </div>

        <div className="mt-3 flex items-stretch gap-2">
          <a
            href={`tel:${business.phone.replace(/\s/g, "")}`}
            className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-xs font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-phone-line text-sm" />
            </span>
            Call
          </a>
          <a
            href={`https://wa.me/${business.whatsapp.replace(/[^0-9]/g, "")}`}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-[#1B9A58] px-3 text-xs font-semibold text-white transition-colors duration-200 hover:bg-[#17854b]"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-whatsapp-line text-sm" />
            </span>
            WhatsApp
          </a>
        </div>
        </div>
      </div>
    </article>
  );
}